/**
 * WellbeingAckBanner — one-time acknowledgement shown the first time
 * a user opens the library or begins an installation. Mar 6 2026.
 *
 * A calm inline banner (never a hard modal) at the top of the page
 * with a single small check: "I understand Soulumo is for reflection
 * and self-guided practice, not medical treatment." Once tapped it
 * writes the ack to localStorage and doesn't return.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const KEY = "soulumo_wellbeing_ack_v1";
export const hasAckedWellbeing = () =>
  typeof window !== "undefined" && window.localStorage.getItem(KEY) === "true";

const TOK = {
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.78)",
  creamMist: "rgba(245,224,173,0.55)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.28)",
  ink: "#0d1f3a",
};

export default function WellbeingAckBanner() {
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState(hasAckedWellbeing());

  if (dismissed) return null;

  const ack = () => {
    try { window.localStorage.setItem(KEY, "true"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="rounded-2xl px-4 py-4 mb-6"
      style={{
        background: "linear-gradient(135deg, rgba(231,201,138,0.10) 0%, rgba(231,201,138,0.03) 100%)",
        border: `1px solid ${TOK.goldMist}`,
      }}
      data-testid="wellbeing-ack-banner"
    >
      <p
        className="italic leading-relaxed"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1rem",
          color: TOK.creamDim,
        }}
      >
        Soulumo is a self-directed reflective audio tool — not medical
        treatment, therapy or diagnosis. Please read the short{" "}
        <button
          onClick={() => nav("/soulumo/wellbeing")}
          className="underline"
          style={{ color: TOK.cream, fontFamily: "inherit" }}
          data-testid="wellbeing-banner-link"
        >
          Notes & Wellbeing
        </button>{" "}
        before your first listen, especially if binaural beats are new
        to you.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={ack}
          data-testid="wellbeing-banner-ack"
          className="rounded-full px-5 py-2"
          style={{
            background: TOK.gold,
            color: TOK.ink,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          · I understand ·
        </button>
        <button
          onClick={() => nav("/soulumo/wellbeing")}
          data-testid="wellbeing-banner-more"
          className="rounded-full px-5 py-2"
          style={{
            background: "transparent",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          Read the notes
        </button>
      </div>
    </div>
  );
}
